/**
 * Listado de una categoria (CAT-01).
 *
 * El `<h1>` es el nombre de la categoria y el titulo SEO sale del backoffice
 * (ADM-08): el administrador puede afinar como aparece en Google sin tocar
 * codigo. Si no lo escribio, se usa el nombre.
 */
import { useParams, useSearchParams } from 'react-router-dom';

import type { OrdenCatalogo } from '@bandu/compartido';

import { ListadoProductos } from '../componentes/catalogo/ListadoProductos';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Migas } from '../componentes/diseno/Migas';
import { useDespacho } from '../contexto/despacho-contexto';
import { usePeticion } from '../hooks/use-peticion';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaListaProductos, esquemaMigas, migasDeCategoria } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerCategoria } from '../servicios/catalogo.servicio';
import { anuncioEntrega } from '../utilidades/entrega';
import { numero } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';

export function Categoria() {
  const { slug = '' } = useParams();
  const [parametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const orden = parametros.get('orden') ?? undefined;
  const { opciones: opcionesDespacho } = useDespacho();

  const { estado, datos, error } = usePeticion(
    (senal) =>
      obtenerCategoria(slug, {
        pagina,
        ...(orden !== undefined ? { orden: orden as OrdenCatalogo } : {}),
        senal,
      }),
    [slug, pagina, orden],
  );

  if (estado === 'cargando') {
    return (
      <div className="contenedor pagina">
        <Cargando que="la categoria" />
      </div>
    );
  }

  if (datos === null) {
    return (
      <div className="contenedor pagina">
        <Seo
          titulo="Categoria no encontrada"
          descripcion="La categoria que buscas no existe en el catalogo."
          ruta={rutas.categoria(slug)}
          sinIndexar
        />
        <AvisoError error={error} />
      </div>
    );
  }

  const { categoria, linea, productos } = datos;
  const migas = migasDeCategoria(linea, categoria);

  return (
    <>
      <Seo
        titulo={categoria.tituloSeo ?? categoria.nombre}
        descripcion={
          categoria.descripcionSeo ??
          `${numero(productos.total)} productos de ${categoria.nombre} con stock real${anuncioEntrega(opcionesDespacho)}.`
        }
        ruta={rutas.categoria(categoria.slug)}
      />
      <DatosEstructurados
        esquemas={[esquemaMigas(migas), esquemaListaProductos(categoria.nombre, productos.items)]}
      />

      <div className="contenedor pagina">
        <Migas migas={migas} />

        <header className="encabezado-pag">
          <div>
            <h1>{categoria.nombre}</h1>
            <p className="sub">
              {linea.nombre} · {numero(productos.total)}{' '}
              {productos.total === 1 ? 'producto' : 'productos'}
            </p>
          </div>
        </header>

        <ListadoProductos
          productos={productos}
          titulo={categoria.nombre}
          ruta={rutas.categoria(categoria.slug)}
        />
      </div>
    </>
  );
}
